import { useState, useEffect, useCallback } from 'react';
import {
    Container,
    Title,
    Card,
    Text,
    Badge,
    Group,
    Stack,
    List,
    Skeleton,
    ActionIcon,
    Tooltip,
} from '@mantine/core';
import { IconRefresh } from '@tabler/icons-react';
import { mcpApi } from '../api/client';

/** Calendar duration since the server started: "1y 2mo 29d 2h 10m" (leading zero units are omitted). */
function formatUptime(seconds: number): string {
    const end = new Date();
    const start = new Date(end.getTime() - seconds * 1000);

    let cursor = new Date(start);
    let years = 0;
    for (;;) {
        const next = new Date(cursor);
        next.setFullYear(cursor.getFullYear() + 1);
        if (next > end) break;
        cursor = next;
        years++;
    }
    let months = 0;
    for (;;) {
        const next = new Date(cursor);
        next.setMonth(cursor.getMonth() + 1);
        if (next > end) break;
        cursor = next;
        months++;
    }
    const rest = Math.floor((end.getTime() - cursor.getTime()) / 60000);
    const days = Math.floor(rest / 1440);
    const hours = Math.floor((rest % 1440) / 60);
    const minutes = rest % 60;

    const parts: [number, string][] = [
        [years, 'y'],
        [months, 'mo'],
        [days, 'd'],
        [hours, 'h'],
        [minutes, 'm'],
    ];
    const first = parts.findIndex(([value]) => value > 0);
    return parts.slice(first === -1 ? parts.length - 1 : first).map(([value, unit]) => `${value}${unit}`).join(' ');
}

export default function MCPStatus() {
    const [status, setStatus] = useState<any>(null);
    const [tools, setTools] = useState<any[]>([]);
    const [loading, setLoading] = useState(true);
    const [refreshing, setRefreshing] = useState(false);
    const [lastUpdated, setLastUpdated] = useState<Date | null>(null);

    const loadData = useCallback(async (manual = false) => {
        if (manual) setRefreshing(true);
        try {
            const [statusData, toolsData] = await Promise.all([
                mcpApi.getStatus(),
                mcpApi.getTools(),
            ]);
            setStatus(statusData);
            setTools(toolsData?.tools ?? []);
            setLastUpdated(new Date());
        } catch (error) {
            console.error('Failed to load MCP data', error);
        } finally {
            setLoading(false);
            setRefreshing(false);
        }
    }, []);

    useEffect(() => {
        loadData();
        const interval = setInterval(() => loadData(), 30_000);
        return () => clearInterval(interval);
    }, [loadData]);

    return (
        <Container size="xl">
            <Group justify="space-between" mb="xl">
                <Title order={1}>MCP Server Status</Title>
                <Group gap="xs">
                    {lastUpdated && (
                        <Text size="xs" c="dimmed">
                            Updated {lastUpdated.toLocaleTimeString()}
                        </Text>
                    )}
                    <Tooltip label="Refresh now">
                        <ActionIcon
                            variant="light"
                            loading={refreshing}
                            onClick={() => loadData(true)}
                            aria-label="Refresh"
                        >
                            <IconRefresh size={16} />
                        </ActionIcon>
                    </Tooltip>
                </Group>
            </Group>

            <Stack gap="lg">
                <Card shadow="sm" padding="lg" radius="md" withBorder>
                    <Text fw={500} size="xl" mb="md">
                        Server Information
                    </Text>
                    {loading ? (
                        <Stack gap="xs">
                            <Skeleton height={28} width={80} />
                            <Skeleton height={20} width={260} />
                        </Stack>
                    ) : (
                        <Group>
                            <Badge color="green" size="lg">
                                Running
                            </Badge>
                            <Text>Version: {status?.version}</Text>
                            <Text>Uptime: {formatUptime(status?.uptime || 0)}</Text>
                            <Text>Tools: {status?.tools_count}</Text>
                        </Group>
                    )}
                </Card>

                <Card shadow="sm" padding="lg" radius="md" withBorder>
                    <Text fw={500} size="xl" mb="md">
                        Available MCP Tools
                    </Text>
                    {loading ? (
                        <Stack gap="sm">
                            {Array.from({ length: 6 }).map((_, i) => (
                                <Skeleton key={i} height={42} />
                            ))}
                        </Stack>
                    ) : (
                        <List>
                            {tools.map((tool) => (
                                <List.Item key={tool.name}>
                                    <Text fw={500}>{tool.name}</Text>
                                    <Text size="sm" c="dimmed">
                                        {tool.description}
                                    </Text>
                                </List.Item>
                            ))}
                        </List>
                    )}
                </Card>
            </Stack>
        </Container>
    );
}
